import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import ScaleIcon from "@mui/icons-material/Scale";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CategoryIcon from "@mui/icons-material/Category";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { Link as RouterLink } from "react-router";
import dayjs from "dayjs";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useRigOfWeek,
  useRigOfWeekPast,
  type RigOfWeekResponse,
} from "../hooks/useApiQueries.js";
import { useAuth } from "../hooks/useAuth.js";
import { api } from "../lib/api.js";
import { SEO } from "../components/SEO.js";
import { MachineHero } from "../components/MachineHero.js";
import { SaveButton } from "../components/SaveButton.js";
import { SpecTileGrid } from "../components/SpecTile.js";
import { NeonEyebrow, NeonDivider, NEON_GLOW_SOFT, neonButtonSx } from "../components/Neon.js";
import { proxied, isLikelyImageUrl } from "../lib/imageProxy.js";
import {
  avatarInitial,
  displaySource,
  formatKg,
  frontendDisplayReason,
  looksLikeDomain,
  sourceLabel,
} from "../lib/displayHelpers.js";

function pickHeroImage(data: RigOfWeekResponse): string | null {
  return data.equipment?.machinery?.primaryImageUrl ?? data.imageUrl ?? null;
}

function pickTitle(data: RigOfWeekResponse): { title: string; brand: string | null } {
  const m = data.equipment?.machinery;
  if (m?.name) return { title: m.name, brand: m.brand?.name ?? null };
  const make = data.equipment?.make;
  const model = data.equipment?.model;
  if (make && model) return { title: `${make} ${model}`, brand: make };
  if (make) return { title: make, brand: null };
  if (model) return { title: model, brand: null };
  return { title: "This Week's Rig", brand: null };
}

export function RigOfWeek() {
  const { data, isLoading } = useRigOfWeek();
  const { data: pastData } = useRigOfWeekPast(8);
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN" || user?.role === "DREW";

  if (isLoading) {
    return (
      <>
        <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="text" width="60%" height={48} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2 }} />
      </>
    );
  }
  if (!data) {
    return (
      <>
        <SEO
          title="Rig of the Week"
          description="The Corian Park team picks the rig of the week every Monday."
          canonical="https://corianpark.au/rig-of-the-week"
        />
        <Box sx={{ py: 8, textAlign: "center" }}>
          <NeonEyebrow glow>RIG OF THE WEEK</NeonEyebrow>
          <Typography variant="h2" sx={{ fontSize: 40, mt: 2, mb: 3, fontWeight: 900 }}>
            Coming Monday
          </Typography>
          <Alert severity="info" sx={{ maxWidth: 520, mx: "auto", textAlign: "left" }}>
            We curate a featured rig every Monday. Browse recent content while you wait —
            we surface the operators and machines doing real work across Australia.
          </Alert>
          <Button
            component={RouterLink}
            to="/content"
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 3, ...neonButtonSx }}
          >
            Browse content
          </Button>
        </Box>
      </>
    );
  }

  const { title, brand } = pickTitle(data);
  const machinery = data.equipment?.machinery ?? null;
  const heroImage = pickHeroImage(data);
  const sourceIsDomain = looksLikeDomain(data.operatorName);
  const sourceDisplay = displaySource(data.operatorName);
  const sourceRoleLabel = sourceLabel(data.operatorName);

  const cleanRationale =
    frontendDisplayReason(data.description) ?? "Our pick — see the source for the full story.";

  // Spec tiles — highlight engine power if available, then weight, year, category.
  const specs = [
    machinery?.enginePowerHp != null && {
      icon: <BoltIcon />,
      label: "Engine power",
      value: `${machinery.enginePowerHp} hp`,
      accent: true,
    },
    machinery?.operatingWeightKg != null && {
      icon: <ScaleIcon />,
      label: "Operating weight",
      value: formatKg(machinery.operatingWeightKg) ?? "—",
    },
    machinery?.year != null && {
      icon: <CalendarMonthIcon />,
      label: "Model year",
      value: String(machinery.year),
    },
    machinery?.category && {
      icon: <CategoryIcon />,
      label: "Category",
      value: machinery.category.name,
    },
  ].filter(Boolean) as Parameters<typeof SpecTileGrid>[0]["tiles"];

  const heroChips = [
    { label: `Week of ${dayjs(data.weekStarting).format("D MMM YYYY")}`, color: "default" as const, icon: <CalendarMonthIcon sx={{ fontSize: 16 }} /> },
    machinery?.category && { label: machinery.category.name, color: "primary" as const },
    data.isSponsored && data.sponsorName && { label: `Supported by ${data.sponsorName}`, color: "white" as const },
  ].filter(Boolean) as Array<{ label: string; color: "default" | "primary" | "white"; icon?: React.ReactNode }>;

  return (
    <>
      <SEO
        title={machinery?.name ? `${machinery.name} — Rig of the Week` : "Rig of the Week"}
        description={machinery?.description ?? cleanRationale}
        canonical="https://corianpark.au/rig-of-the-week"
        ogImage={heroImage ?? undefined}
        type="article"
        article={{ publishedTime: dayjs(data.weekStarting).toISOString(), author: "Corian Park" }}
      />

      {/* Bleed escape — hero punches through Layout's lg container into a wider band */}
      <Box sx={{ mx: { xs: -2, sm: -3 }, mt: -4, mb: { xs: 3, md: 5 }, position: "relative" }}>
        <MachineHero
          imageUrl={heroImage}
          eyebrow="RIG OF THE WEEK"
          brand={brand}
          title={title}
          subtitle={
            sourceDisplay
              ? sourceIsDomain
                ? `Reported by ${sourceDisplay}`
                : `Spotted on ${sourceDisplay}`
              : null
          }
          chips={heroChips}
        />
        {isAdmin && (
          <IconButton
            component={RouterLink}
            to="/dashboard/rig-of-week"
            aria-label="Edit in dashboard"
            sx={{
              position: "absolute",
              top: { xs: 8, md: 16 },
              right: { xs: 8, md: 16 },
              bgcolor: "primary.main",
              color: "common.black",
              boxShadow: NEON_GLOW_SOFT,
              "&:hover": { bgcolor: "primary.main", boxShadow: "0 0 18px rgba(255,176,0,0.85)" },
            }}
          >
            <EditIcon />
          </IconButton>
        )}
        {!isAdmin && data && (
          <Box
            sx={{
              position: "absolute",
              top: { xs: 8, md: 16 },
              right: { xs: 8, md: 16 },
            }}
          >
            <SaveButton itemType="rig" itemId={data.id} variant="overlay" size="medium" />
          </Box>
        )}
      </Box>

      {isAdmin && data && <AdminEditPanel rig={data} />}

      {specs.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <SpecTileGrid tiles={specs} />
        </Box>
      )}

      {data.sourceContent && (
        <Box sx={{ mb: 5 }}>
          <NeonEyebrow>SEE IT IN ACTION</NeonEyebrow>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "16 / 9",
              mt: 2,
              borderRadius: 1,
              overflow: "hidden",
              boxShadow: "0 0 40px -10px rgba(255,176,0,0.25)",
            }}
          >
            <Box
              component="iframe"
              src={data.sourceContent.embedUrl}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
            />
          </Box>
          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {data.sourceContent.title}
            </Typography>
            <Link
              component={RouterLink}
              to={`/content/${data.sourceContent.slug}`}
              sx={{ fontSize: 12, color: "primary.main", ml: "auto" }}
            >
              Read full article →
            </Link>
          </Box>
        </Box>
      )}

      <Grid container spacing={{ xs: 3, md: 4 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <NeonEyebrow>WHY THIS RIG</NeonEyebrow>
          <Typography
            variant="h3"
            sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 800, mt: 1, mb: 2 }}
          >
            What earned it the spot
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 17, lineHeight: 1.7 }}>
            {cleanRationale}
          </Typography>

          {machinery?.description && (
            <>
              <NeonDivider width={64} sx={{ my: 3 }} />
              <NeonEyebrow>ABOUT THE MACHINE</NeonEyebrow>
              <Typography
                variant="body1"
                sx={{ fontSize: 16, lineHeight: 1.7, color: "text.secondary", mt: 1.5 }}
              >
                {machinery.description}
              </Typography>
            </>
          )}

          {machinery?.slug && data.equipment?.confirmed && (
            <Box sx={{ mt: 3 }}>
              <Link
                component={RouterLink}
                to={`/equipment/${machinery.slug}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  fontWeight: 700,
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { textShadow: "0 0 12px rgba(255,176,0,0.6)" },
                }}
              >
                See every video &amp; article featuring the {machinery.model ?? machinery.name}
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </Link>
            </Box>
          )}

          {/* CTA card — convert reader interest into a quote */}
          <Box
            sx={{
              mt: 5,
              p: 3,
              borderRadius: 1,
              background:
                "linear-gradient(135deg, rgba(255,176,0,0.12) 0%, rgba(255,136,0,0.04) 70%, transparent 100%)",
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 0 30px -10px rgba(255,176,0,0.5)",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Need work like this done?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  The Corian Park team routes earthworks quotes to the right operator across Australia.
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/quote"
                variant="contained"
                color="primary"
                size="large"
                startIcon={<RequestQuoteIcon />}
                sx={{ ...neonButtonSx, flexShrink: 0 }}
              >
                Get a quote
              </Button>
            </Stack>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Box
            sx={{
              position: { md: "sticky" },
              top: { md: 24 },
              p: 3,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              background:
                "linear-gradient(180deg, rgba(255,176,0,0.05) 0%, transparent 100%)",
            }}
          >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "common.black",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 900,
                  fontSize: 20,
                  flexShrink: 0,
                  boxShadow: "0 0 16px rgba(255,176,0,0.55)",
                }}
              >
                {avatarInitial(sourceDisplay)}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1 }}>
                  {sourceRoleLabel === "Source" ? "Reported by" : "Featured operator"}
                </Typography>
                <Typography variant="h6" sx={{ lineHeight: 1.2, wordBreak: "break-word" }}>
                  {sourceDisplay ?? "Unknown"}
                </Typography>
              </Box>
            </Stack>

            {(brand || machinery?.model) && (
              <>
                <Divider sx={{ mb: 2 }} />
                <NeonEyebrow>RUNNING</NeonEyebrow>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                  {brand && (
                    <Chip
                      label={brand}
                      size="small"
                      sx={{ bgcolor: "action.hover", fontWeight: 700 }}
                    />
                  )}
                  {machinery?.model && machinery.model !== brand && (
                    <Chip label={machinery.model} size="small" variant="outlined" />
                  )}
                </Stack>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <NeonEyebrow>HOW WE PICKED</NeonEyebrow>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Selected from this week's highest-scoring approved content across engines,
              operator consistency and the quality of the machine work on show. The Corian Park
              team makes the final call every Monday.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {pastData && pastData.items.length > 0 && (
        <PastRigsStrip items={pastData.items} />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Admin inline edit panel — only renders when the viewer is ADMIN/DREW.
// Lets Drew fix the operator label, the rationale, and the image URL
// without leaving the public page. Calls the existing PATCH endpoint.
// ────────────────────────────────────────────────────────────────
function AdminEditPanel({ rig }: { rig: RigOfWeekResponse }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [operatorName, setOperatorName] = useState(rig.operatorName ?? "");
  const [description, setDescription] = useState(rig.description ?? "");
  const [imageUrl, setImageUrl] = useState(rig.imageUrl ?? "");

  const mutation = useMutation({
    mutationFn: () =>
      api(`/v1/dashboard/rig-of-week/${rig.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          selectedOperatorName: operatorName.trim() || undefined,
          rigDescription: description.trim() || undefined,
          rigImageUrl: imageUrl.trim() || null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rig-of-week"] });
      setOpen(false);
    },
  });

  return (
    <Box
      sx={{
        mb: 4,
        border: "1px dashed",
        borderColor: "primary.main",
        borderRadius: 1,
        bgcolor: "rgba(255,176,0,0.04)",
        boxShadow: NEON_GLOW_SOFT,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          cursor: "pointer",
          "&:hover": { bgcolor: "rgba(255,176,0,0.08)" },
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <DashboardIcon sx={{ color: "primary.main" }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" color="primary.main" sx={{ lineHeight: 1, fontWeight: 700 }}>
            ADMIN · INLINE EDIT
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {open ? "Editing — make changes below" : "Quick edit operator, rationale, image"}
          </Typography>
        </Box>
        <IconButton size="small" aria-label={open ? "Close" : "Open"} sx={{ color: "primary.main" }}>
          {open ? <CloseIcon /> : <EditIcon />}
        </IconButton>
      </Box>
      {open && (
        <Box sx={{ p: 2.5, pt: 0 }}>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(mutation.error as Error).message}
            </Alert>
          )}
          {mutation.isSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Saved.
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Operator / source"
              value={operatorName}
              onChange={(e) => setOperatorName(e.target.value)}
              size="small"
              fullWidth
              helperText="The label shown in the hero subtitle and sidebar."
            />
            <TextField
              label="Why this rig (rationale)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
            />
            <TextField
              label="Hero image URL"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              size="small"
              fullWidth
              helperText="Paste a publisher or Tractrs image URL. Leave blank to fall back to machinery image."
            />
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
                sx={neonButtonSx}
              >
                {mutation.isPending ? "Saving…" : "Save"}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setOperatorName(rig.operatorName ?? "");
                  setDescription(rig.description ?? "");
                  setImageUrl(rig.imageUrl ?? "");
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                component={RouterLink}
                to="/dashboard/rig-of-week"
                startIcon={<DashboardIcon />}
                size="small"
                sx={{ color: "primary.main" }}
              >
                Open dashboard
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────
// Past rigs strip — chronologically descending list of previous weeks.
// Cards link to /rig-of-the-week (which currently always shows the
// latest); the past-rig direct view is a future enhancement.
// ────────────────────────────────────────────────────────────────
function PastRigsStrip({
  items,
}: {
  items: NonNullable<ReturnType<typeof useRigOfWeekPast>["data"]>["items"];
}) {
  return (
    <Box sx={{ mt: 6 }}>
      <NeonDivider sx={{ mb: 4 }} />
      <Stack direction="row" spacing={2} sx={{ alignItems: "baseline", mb: 1 }}>
        <NeonEyebrow sx={{ mb: 0 }}>PREVIOUS WEEKS</NeonEyebrow>
        <Link
          component={RouterLink}
          to="/rig-of-week/archive"
          sx={{ fontSize: 13, color: "text.secondary", ml: "auto !important" }}
        >
          View all →
        </Link>
      </Stack>
      <Typography variant="h3" sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, mt: 1, mb: 3 }}>
        Past Rigs of the Week
      </Typography>
      <Grid container spacing={2}>
        {items.map((p) => {
          const equipLabel =
            (p.equipment?.make && p.equipment?.model
              ? `${p.equipment.make} ${p.equipment.model}`
              : p.equipment?.make ?? p.equipment?.model) ??
            p.operatorName ??
            "Past rig";
          const subtitle = displaySource(p.operatorName) ?? "";
          const img = p.imageUrl;
          return (
            <Grid key={p.id} size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  borderRadius: 1,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: NEON_GLOW_SOFT,
                    borderColor: "primary.main",
                  },
                }}
              >
                {isLikelyImageUrl(img) ? (
                  <Box sx={{ position: "relative", aspectRatio: "16 / 10" }}>
                    <Box
                      component="img"
                      src={proxied(img!)}
                      alt={equipLabel}
                      loading="lazy"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      aspectRatio: "16 / 10",
                      bgcolor: "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      No image
                    </Typography>
                  </Box>
                )}
                <Box sx={{ p: 2, flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1 }}
                  >
                    Week of {dayjs(p.weekStarting).format("D MMM YYYY")}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2 }}>
                    {equipLabel}
                  </Typography>
                  {subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
